import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Job, JobInstance, JobParameter, InstanceParameter } from '../../../models/job';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-job-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    TranslocoPipe,
  ],
  templateUrl: './job-details-dialog.component.html',
  styleUrls: ['./job-details-dialog.component.scss'],
})
export class JobDetailsDialogComponent implements OnInit {
  job: Job | null = null;

  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private translocoService = inject(TranslocoService);

  ngOnInit(): void {
    this.job = this.config.data?.job || null;
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translocoService.translate(key, params);
  }

  onClose(): void {
    this.ref.close();
  }

  getStatusColor(status?: string): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'disabled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  }

  getStatusIcon(status?: string): string {
    switch (status) {
      case 'active':
        return 'fa-solid fa-check-circle';
      case 'inactive':
        return 'fa-solid fa-pause-circle';
      case 'draft':
        return 'fa-solid fa-edit';
      case 'disabled':
        return 'fa-solid fa-ban';
      default:
        return 'fa-solid fa-question-circle';
    }
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case 'active':
        return this.translate('jobsPage.status.active');
      case 'inactive':
        return this.translate('jobsPage.status.inactive');
      case 'draft':
        return this.translate('jobsPage.status.draft');
      case 'disabled':
        return this.translate('jobsPage.status.disabled');
      default:
        return this.translate('jobsPage.status.unknown');
    }
  }

  getInstanceStatusLabel(status?: string): string {
    switch (status) {
      case 'active':
        return this.translate('jobsPage.instances.status.active');
      case 'inactive':
        return this.translate('jobsPage.instances.status.inactive');
      case 'draft':
        return this.translate('jobsPage.instances.status.draft');
      case 'disabled':
        return this.translate('jobsPage.instances.status.disabled');
      default:
        return this.translate('jobsPage.instances.status.unknown');
    }
  }

  getJobTypeLabel(jobType?: string): string {
    switch (jobType) {
      case 'activate':
        return this.translate('jobDetailsDialog.jobTypes.activate');
      case 'run':
        return this.translate('jobDetailsDialog.jobTypes.run');
      case 'chat':
        return this.translate('jobDetailsDialog.jobTypes.chat');
      case 'scheduled':
        return this.translate('jobDetailsDialog.jobTypes.scheduled');
      default:
        return jobType ? jobType : this.translate('jobsPage.status.unknown');
    }
  }

  getRatingStars(rating?: number): { filled: number; half: boolean; empty: number } {
    if (!rating) {
      return { filled: 0, half: false, empty: 5 };
    }
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    return { filled: fullStars, half: hasHalfStar, empty: emptyStars };
  }

  getFilledStars(rating?: number): number[] {
    const stars = this.getRatingStars(rating);
    return Array(stars.filled).fill(0).map((_, i) => i);
  }

  getEmptyStars(rating?: number): number[] {
    const stars = this.getRatingStars(rating);
    return Array(stars.empty).fill(0).map((_, i) => i);
  }

  getInstanceParameterLabel(instanceParam: InstanceParameter): string {
    if (!this.job?.parameters) {
      return instanceParam.label || this.translate('jobDetailsDialog.labels.unknown');
    }
    const jobParam = this.job.parameters.find((p) => p.uuid === instanceParam.parameterUuid);
    return jobParam?.label || instanceParam.label || this.translate('jobDetailsDialog.labels.unknown');
  }

  getInstanceParameterValue(instanceParam: InstanceParameter): any {
    return instanceParam.value !== null && instanceParam.value !== undefined
      ? instanceParam.value
      : this.translate('jobDetailsDialog.labels.notAvailable');
  }
}

